import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { 
  LayoutDashboard, 
  FileText, 
  Gavel, 
  Stethoscope, 
  Fuel, 
  ClipboardList,
  TrendingUp,
  LogOut,
  Menu,
  X,
  User,
  ChevronRight,
  Users,
  ShieldCheck,
  Lock
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard' },
  { path: '/processos', label: 'Processos', icon: FileText, module: 'processos' },
  { path: '/licitacoes', label: 'Licitações', icon: Gavel, module: 'licitacoes' },
  { path: '/especialistas', label: 'Especialistas', icon: Stethoscope, module: 'especialistas' },
  { path: '/abastecimento', label: 'Abastecimento', icon: Fuel, module: 'abastecimento' },
  { path: '/atendimentos', label: 'Atendimentos', icon: ClipboardList, module: 'atendimentos' },
  { path: '/indicadores', label: 'Indicadores', icon: TrendingUp, module: 'indicadores' },
]

const adminMenuItems = [
  { path: '/usuarios', label: 'Usuários', icon: Users, module: 'usuarios' },
]

export default function Layout({ children }) {
  const { user, logout, isAdmin, hasPermission } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleMenuClick = (e, item) => {
    const hasAccess = isAdmin || hasPermission(item.module)
    if (!hasAccess) {
      e.preventDefault()
      toast.error('Acesso negado', {
        description: `Você não tem permissão para acessar o módulo "${item.label}". Solicite ao administrador.`
      })
      return
    }
    setSidebarOpen(false)
  }

  const allMenuItems = isAdmin ? [...menuItems, ...adminMenuItems] : menuItems

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="h-20 flex items-center gap-3 px-4 border-b border-slate-100 bg-white">
          <img 
            src="/logo-pao-acucar.png" 
            alt="Prefeitura Pão de Açúcar" 
            className="h-14 w-auto object-contain"
          />
          <button 
            className="lg:hidden ml-auto text-slate-600"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {allMenuItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
            const Icon = item.icon
            const isAdminItem = adminMenuItems.some(ai => ai.path === item.path)
            const hasAccess = isAdmin || hasPermission(item.module)
            
            return (
              <Link
                key={item.path}
                to={hasAccess ? item.path : '#'}
                onClick={(e) => handleMenuClick(e, item)}
                data-testid={`nav-${item.path.replace('/', '')}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  !hasAccess && "opacity-50 cursor-not-allowed",
                  isActive && hasAccess
                    ? "bg-teal-50 text-teal-700 border-l-4 border-teal-600 -ml-1 pl-5" 
                    : hasAccess 
                      ? "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                      : "text-slate-400"
                )}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
                {!hasAccess && <Lock className="w-3 h-3 text-slate-400 ml-auto" />}
                {isAdminItem && hasAccess && <ShieldCheck className="w-3 h-3 text-blue-500 ml-auto" />}
                {isActive && hasAccess && !isAdminItem && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            )
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-slate-50">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-teal-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.nome}</p>
              <div className="flex items-center gap-2">
                <p className="text-xs text-slate-500 truncate">{user?.cargo}</p>
                {isAdmin && (
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1 py-0">
                    Admin
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-600 hover:text-rose-600 hover:bg-rose-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8">
          <button 
            className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="ml-auto flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:block">
              {new Date().toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  )
}
